import React from 'react';
import { carouselTrack, carouselTrackVariants, combineCarouselClasses } from './carousel.styles';
import type { CarouselTrackProps, CarouselResponsiveVariant } from './types';

/**
 * Helper function to resolve responsive variant values
 * In a full implementation, this would handle breakpoint matching
 */
function resolveResponsiveValue<T>(value: T | CarouselResponsiveVariant<T>): T {
  // Check if it's a responsive variant object (not a primitive value)
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const responsive = value as Record<string, T | undefined>;
    // Return base value if available, otherwise return the first defined value
    return responsive.base ?? (Object.values(responsive).find((v) => v !== undefined) as T) ?? (true as T);
  }
  return value as T;
}

/**
 * CarouselTrack - Core scrolling container using semantic <ul> element
 *
 * This is the main scrolling container that holds all carousel items.
 * Implements horizontal scrolling with optional snap alignment and keyboard navigation.
 * Uses semantic <ul> element for proper screen reader list navigation.
 */
export const CarouselTrack = React.forwardRef<HTMLUListElement, CarouselTrackProps>(
  (
    {
      gap = 'md',
      snapAlign = 'start',
      scrollBehavior = 'smooth',
      enableSnap = true,
      children,
      className,
      onScroll,
      ...restProps
    },
    ref,
  ) => {
    const resolvedSnap = resolveResponsiveValue(enableSnap);

    // Handle keyboard navigation
    const handleKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
      const track = event.currentTarget;
      const scrollAmount = 200; // Amount to scroll on arrow key press

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          track.scrollBy({ left: -scrollAmount, behavior: scrollBehavior });
          break;

        case 'ArrowRight':
          event.preventDefault();
          track.scrollBy({ left: scrollAmount, behavior: scrollBehavior });
          break;

        case 'Home':
          event.preventDefault();
          track.scrollTo({ left: 0, behavior: scrollBehavior });
          break;

        case 'End':
          event.preventDefault();
          track.scrollTo({ left: track.scrollWidth, behavior: scrollBehavior });
          break;

        default:
          // Let other keys pass through normally
          break;
      }
    };

    // Combine base styles with variants
    const trackClasses = combineCarouselClasses(
      carouselTrack,
      carouselTrackVariants({
        gap,
        snapAlign: resolvedSnap ? snapAlign : 'none',
        scrollBehavior,
      }),
      className,
    );

    return (
      <ul
        ref={ref}
        className={trackClasses}
        onScroll={onScroll}
        onKeyDown={handleKeyDown}
        role="listbox"
        aria-orientation="horizontal"
        tabIndex={0}
        {...restProps}
      >
        {children}
      </ul>
    );
  },
);

CarouselTrack.displayName = 'CarouselTrack';
